import { Component, Fragment } from "react";
import * as React from "react";
import { Icon } from "office-ui-fabric-react";
import School, { License, OnboardStatus, licenseLevelIsAtLeast } from "../../../models/School";
import PageTitle from "../../PageTitle";
import { Link } from "react-router-dom";
import ApiService from "../../ApiService";

export interface OnboardTimelineItem {
    key: string;
    name: string;

    next?: string;
    previous?: string;

    completed: boolean;
}

export const urlForTimelineItem = (tenantId: string, schoolCode: string, timelineItem: OnboardTimelineItem) => `/manage/${tenantId}/${schoolCode}/onboard/${timelineItem.key}`;

interface OnboardTimelineProps {
    currentItemKey?: string;

    tenantId: string;
    schoolCode: string;

    apiService: ApiService;

    children(school: School, timelineItems: OnboardTimelineItem[]): any;
}

interface OboardTimelineState {
    numberOfTimelineItemsToShow: number;

    school?: School;
    items?: OnboardTimelineItem[];
    loading: boolean;
}

export class OnboardTimelineState {

    public static getCurrentOnboardTimeline(schoolOnboardStatus: OnboardStatus | undefined, schoolLicense: License | undefined): OnboardTimelineItem[] {
        let timelineItems: OnboardTimelineItem[] = [];

        if (schoolOnboardStatus === undefined || schoolLicense === undefined) {
            //TODO: Error
            return [];
        }

        const preSdsItems = [
            {
                key: "mega-menu",
                name: "Mega Menu",
                completed: false
            },
            {
                key: "school-theme",
                name: "Theme and Logo",
                completed: false
            }
        ];

        const completedPreSdsItems = preSdsItems.map(i => Object.assign({}, i, { completed: true }));

        switch (schoolOnboardStatus) {
            case "notStarted": {
                timelineItems = [
                    {
                        key: "not-started",
                        name: "Start Onboarding",
                        completed: false
                    }
                ]
                break;
            }
            case "started": {
                timelineItems = [
                    ...preSdsItems,
                    {
                        key: "run-school-build",
                        name: "SchoolBuild Playlist",
                        completed: false,
                    },
                    OnboardTimelineState.GetCompleteBuildItem(schoolLicense)
                ];
                break;
            }
            case "sdsDataAvailable": {
                timelineItems = [
                    ...completedPreSdsItems,
                    {
                        key: "sds-dates",
                        name: "Set SDS Dates",
                        completed: false
                    },
                    {
                        key: "ready-for-consultant",
                        name: "Ready For Consultant",
                        completed: false
                    }
                ];
                break;
            }
            case "consultancy": {
                timelineItems = [
                    ...completedPreSdsItems,
                    {
                        key: "subject-exclude",
                        name: "Exclude Subjects",
                        completed: false
                    },
                    {
                        key: "subject-merge",
                        name: "Merge Subjects",
                        completed: false
                    },
                    {
                        key: "subject-rename",
                        name: "Rename Subjects",
                        completed: false
                    },
                    {
                        key: "year-grouping",
                        name: "Year Groupings",
                        completed: false
                    },
                    {
                        key: "year-grouping-years",
                        name: "Assign Years",
                        completed: false
                    },
                    {
                        key: "department",
                        name: "Departments",
                        completed: false
                    },
                    {
                        key: "department-subjects",
                        name: "Department Subjects",
                        completed: false
                    },
                    {
                        key: "extra-teams",
                        name: "Extra Teams",
                        completed: false
                    },
                    {
                        key: "run-silver",
                        name: "Silver Playlist",
                        completed: false,
                    },
                    {
                        key: "complete-onboarding",
                        name: "Complete Onboarding",
                        completed: false
                    }
                ];


                break;
            }
            case "completed": {
                timelineItems = [
                    {
                        key: "completed",
                        name: "Onboarding Completed",
                        completed: true
                    }
                ];
                break;
            }
            default: {
                timelineItems = [];
                break;
            }
        }

        return timelineItems;
    }

    private static GetCompleteBuildItem(schoolLicense: License) {
        if (licenseLevelIsAtLeast(schoolLicense, "silver")) {
            // Silver and above
            return {
                key: "complete-build-sds",
                name: "Complete SDS Build",
                completed: false
            };
        }
        else {
            // Foundation
            return {
                key: "complete-build-foundation",
                name: "Complete Foundation Build",
                completed: false
            };
        }
    }
}

export default class OnboardTimeline extends Component<OnboardTimelineProps, OboardTimelineState>
{
    constructor(props: OnboardTimelineProps) {
        super(props);
        this.state = {
            numberOfTimelineItemsToShow: 100, // Show all as default

            school: undefined,
            items: undefined,
            loading: true
        };
    }

    private async load() {
        this.setState({ loading: true });

        const school = await this.props.apiService.getSchool(this.props.tenantId, this.props.schoolCode);
        const items = OnboardTimelineState.getCurrentOnboardTimeline(school.onboardStatus, school.license);

        this.setState({
            school: school,
            items: items,
            loading: false
        })

    }

    public async componentDidMount() {
        this.updateNumberOfTimelineItemsToShow();
        window.addEventListener('resize', () => this.updateNumberOfTimelineItemsToShow());

        await this.load();
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', () => this.updateNumberOfTimelineItemsToShow());
    }

    private updateNumberOfTimelineItemsToShow() {
        const width = document.getElementById('onboard-timeline')?.clientWidth;
        if (width !== undefined) {
            let numberOfTimelineItemsToShow = width / 200;

            numberOfTimelineItemsToShow = 2 * Math.floor(numberOfTimelineItemsToShow / 2) + 1; // Rounds to nearest odd number

            numberOfTimelineItemsToShow = Math.max(numberOfTimelineItemsToShow, 1);

            this.setState({
                numberOfTimelineItemsToShow
            });
        }
    }

    private getVisible(index: number, activeIndex: number, count: number): boolean {
        const sideCount = (this.state.numberOfTimelineItemsToShow - 1) / 2;
        let before = sideCount;
        let after = sideCount;

        const beforeOffset = activeIndex - before;
        const afterOffset = count - activeIndex - after - 1;

        if (beforeOffset < 0) {
            after -= beforeOffset;
        }

        if (afterOffset < 0) {
            before -= afterOffset;
        }

        return activeIndex - before <= index && activeIndex + after >= index;
    }

    public render() {


        if (this.state.loading) {
            return null;
        }

        const activeIndex = this.state.items!.findIndex(item => item.key === this.props.currentItemKey);

        const timelineItems = this.state.items!.map((item: OnboardTimelineItem, index) => {
            const completedClass = item.completed ? `onboard-timeline-item--completed` : "";

            const ItemComponent = index < activeIndex ? Link : "div"; // allow direct navigation to previous timeline items
            const activeClass = item.key === this.props.currentItemKey ? `onboard-timeline-item--active` : "";
            return (
                <ItemComponent key={index} to={urlForTimelineItem(this.props.tenantId, this.props.schoolCode, item)} className={`onboard-timeline-item ${completedClass} ${activeClass}`}>
                    <div className={"onboard-timeline-item__indicator-container"}>
                        {index === 0
                            ? <div className="onboard-timeline-gap" />
                            : <div className="onboard-timeline-line" />}

                        <div className={"onboard-timeline-item__indicator"}>
                            {item.completed ? <Icon iconName="Accept" className="" /> : null}
                        </div>

                        {index < this.state.items!.length - 1
                            ? <div className="onboard-timeline-line" />
                            : <div className="onboard-timeline-gap" />}
                    </div>
                    <div className={"onboard-timeline-item__name"}>{item.name}</div>
                </ItemComponent>
            );
        });

        const connectedTimelineItems = timelineItems.reduce((acc: any, item: JSX.Element, index: number) => {
            if (this.getVisible(index, activeIndex, this.state.items!.length)) {
                acc.push(item);
            }
            return acc;
        }, []);

        const activeItem = this.state.items![activeIndex];

        return (
            <Fragment>
                <PageTitle title={`Onboarding : ${activeItem?.name}`} />
                <div id="onboard-timeline" className={"onboard-timeline"}>
                    {connectedTimelineItems}
                </div>
                {this.props.children ? this.props.children(this.state.school!, this.state.items!) : null}
            </Fragment>
        );
    }
}